/**
 * src/services/spatial/spatialPresenceTypes.ts
 *
 * Types for spatial object presence — the AI and media capabilities a
 * building, floor or zone can be given.
 *
 * Mirrors GeoLink migrations 047-049. Nothing here grants anything: a
 * configuration says what an object is allowed to attempt, and the
 * GeoTrust runtime still checks contract authority, country policy and
 * execution-rule state before any of it takes effect.
 */

/** How a conversation with an object may begin. */
export type SpatialInteractionMode =
  | 'passive'
  | 'proximity'
  | 'explicit'
  | 'event'
  | 'execution_rule';

/** What may cause a broadcast to start. */
export type SpatialBroadcastTriggerMode =
  | 'manual'
  | 'proximity'
  | 'event'
  | 'execution_rule'
  | 'ai_action';

/** Where a broadcast claims to originate. Never a browser GPS fix. */
export type SpatialBroadcastAnchorType =
  | 'centroid'
  | 'entrance'
  | 'point'
  | 'floor'
  | 'zone'
  | 'custom';

export type SpatialTtsInterruptMode = 'queue' | 'interrupt' | 'replace';
export type SpatialTtsQueueMode = 'queue' | 'drop_oldest' | 'drop_newest';
export type SpatialVolumeCurve = 'none' | 'linear' | 'smoothstep';

/** AI presence configuration for one object. */
export interface SpatialObjectAgentConfig {
  id: string;
  objectId: string;
  enabled: boolean;

  displayName: string | null;
  persona: string | null;
  systemPrompt: string | null;
  greetingTemplate: string | null;

  interactionMode: SpatialInteractionMode;
  proximityRadiusMeters: number;
  altitudeToleranceMeters: number;
  autoInitiate: boolean;

  cooldownSeconds: number;
  maxConversationDurationSeconds: number;
  maxMessagesPerSession: number;

  allowHumanUsers: boolean;
  allowAiAgents: boolean;
  requireConnectedWallet: boolean;

  executionRuleId: number | null;
  countryPolicyAware: boolean;
  toolsAllowed: string[];

  /** Wallet whose on-chain authority autonomous behaviour rests on. */
  authorizedByWallet: string | null;
  authorizedAt: string | null;

  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/** Media presence configuration for one object. */
export interface SpatialObjectMediaConfig {
  id: string;
  objectId: string;
  enabled: boolean;

  allowLiveBroadcast: boolean;
  autoStartBroadcast: boolean;
  broadcastTriggerMode: SpatialBroadcastTriggerMode;
  executionRuleId: number | null;
  broadcastTitleTemplate: string | null;
  broadcastDescriptionTemplate: string | null;
  visibility: 'public' | 'groups';
  maxBroadcastDurationSeconds: number;
  idleTimeoutSeconds: number;
  broadcastAnchorType: SpatialBroadcastAnchorType;
  broadcastAnchorPoint: { type: 'Point'; coordinates: [number, number] } | null;

  ttsEnabled: boolean;
  voiceId: string | null;
  language: string | null;
  speakingStyle: string | null;
  playbackRate: number;
  maxSpeechCharacters: number;
  interruptMode: SpatialTtsInterruptMode;
  queueMode: SpatialTtsQueueMode;

  proximityPlaybackEnabled: boolean;
  playbackRadiusMeters: number;
  /** Strictly greater than the playback radius; the hysteresis band. */
  exitRadiusMeters: number;
  altitudeToleranceMeters: number;
  floorAware: boolean;
  zoneAware: boolean;
  autoSurfacePlayer: boolean;
  /** Advisory. Browser autoplay policy still decides. */
  autoPlayAudio: boolean;
  minDistanceMeters: number | null;
  maxDistanceMeters: number | null;
  volumeCurve: SpatialVolumeCurve;
  stopOnExit: boolean;
  fadeOnExit: boolean;
  cooldownSeconds: number;

  allowHumanListeners: boolean;
  allowAiAgentListeners: boolean;
  requireConnectedWallet: boolean;
  countryPolicyAware: boolean;

  authorizedByWallet: string | null;
  authorizedAt: string | null;

  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/** Pre-recorded media attached to an object, stored in the GeoTrust VOD store. */
export interface SpatialObjectMediaAsset {
  id: string;
  objectId: string;
  recordingId: string;
  title: string | null;
  description: string | null;
  kind: 'video' | 'audio';
  durationMs: number | null;
  orderIndex: number;
  visibility: 'public' | 'restricted' | 'private';
  uploadedByWallet: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/** What may cause a surface to light up. */
export type SpatialSurfaceActivationMode =
  | 'manual'
  | 'event'
  | 'execution_rule'
  | 'ai_action'
  | 'proximity';

export type SpatialSurfaceKind = 'facade' | 'screen' | 'projection' | 'lighting';

/**
 * Surface presence configuration for one object.
 *
 * Deliberately separate from media: a facade lighting up and a building
 * speaking are different capabilities, and an operator may well want one
 * without the other.
 */
export interface SpatialObjectSurfaceConfig {
  id: string;
  objectId: string;
  enabled: boolean;

  defaultContentRef: string | null;
  activationMode: SpatialSurfaceActivationMode;
  executionRuleId: number | null;

  maxActivationSeconds: number;
  cooldownSeconds: number;

  /**
   * Content the operator pre-approved. An activation naming anything
   * outside this list is refused — including one an AI proposed.
   */
  allowedContentRefs: Array<string | { ref: string; label?: string }>;

  surfaceKind: SpatialSurfaceKind;
  orientationDegrees: number | null;
  widthMeters: number | null;
  heightMeters: number | null;

  authorizedByWallet: string | null;
  authorizedAt: string | null;

  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/** What a surface is showing right now, as GeoTrust's runtime reports it. */
export interface SpatialSurfaceActivation {
  id: string;
  spatialObjectId: string;
  objectName: string | null;
  surfaceKind: SpatialSurfaceKind;
  contentRef: string;
  trigger: string;
  executionRuleId: number | null;
  activatedAt: string;
  expiresAt: string;
  authorizedByWallet: string | null;
}

/**
 * Partial update bodies. Only the keys present are written, so a UI
 * that renders one section cannot reset another.
 */
export type SpatialObjectAgentConfigPatch =
  Partial<Omit<SpatialObjectAgentConfig, 'id' | 'objectId' | 'authorizedAt' | 'createdAt' | 'updatedAt'>>;

export type SpatialObjectMediaConfigPatch =
  Partial<Omit<SpatialObjectMediaConfig, 'id' | 'objectId' | 'authorizedAt' | 'createdAt' | 'updatedAt'>>;

export type SpatialObjectSurfaceConfigPatch =
  Partial<Omit<SpatialObjectSurfaceConfig, 'id' | 'objectId' | 'authorizedAt' | 'createdAt' | 'updatedAt'>>;

/** Defaults matching the migration defaults, for rendering an unsaved form. */
export const DEFAULT_AGENT_CONFIG: SpatialObjectAgentConfigPatch = {
  enabled: false,
  displayName: null,
  persona: null,
  systemPrompt: null,
  greetingTemplate: null,
  interactionMode: 'passive',
  proximityRadiusMeters: 100,
  altitudeToleranceMeters: 25,
  autoInitiate: false,
  cooldownSeconds: 60,
  maxConversationDurationSeconds: 600,
  maxMessagesPerSession: 20,
  allowHumanUsers: true,
  allowAiAgents: false,
  requireConnectedWallet: false,
  executionRuleId: null,
  countryPolicyAware: true,
  toolsAllowed: [],
};

export const DEFAULT_MEDIA_CONFIG: SpatialObjectMediaConfigPatch = {
  enabled: false,
  allowLiveBroadcast: false,
  autoStartBroadcast: false,
  broadcastTriggerMode: 'manual',
  executionRuleId: null,
  broadcastTitleTemplate: null,
  broadcastDescriptionTemplate: null,
  visibility: 'public',
  maxBroadcastDurationSeconds: 1800,
  idleTimeoutSeconds: 300,
  broadcastAnchorType: 'centroid',
  broadcastAnchorPoint: null,
  ttsEnabled: false,
  voiceId: null,
  language: null,
  speakingStyle: null,
  playbackRate: 1,
  maxSpeechCharacters: 400,
  interruptMode: 'queue',
  queueMode: 'queue',
  proximityPlaybackEnabled: false,
  playbackRadiusMeters: 100,
  exitRadiusMeters: 115,
  altitudeToleranceMeters: 25,
  floorAware: false,
  zoneAware: false,
  autoSurfacePlayer: true,
  autoPlayAudio: false,
  minDistanceMeters: null,
  maxDistanceMeters: null,
  volumeCurve: 'smoothstep',
  stopOnExit: true,
  fadeOnExit: true,
  cooldownSeconds: 120,
  allowHumanListeners: true,
  allowAiAgentListeners: false,
  requireConnectedWallet: false,
  countryPolicyAware: true,
};

export const DEFAULT_SURFACE_CONFIG: SpatialObjectSurfaceConfigPatch = {
  enabled: false,
  defaultContentRef: null,
  activationMode: 'manual',
  executionRuleId: null,
  maxActivationSeconds: 3600,
  cooldownSeconds: 0,
  allowedContentRefs: [],
  surfaceKind: 'facade',
  orientationDegrees: null,
  widthMeters: null,
  heightMeters: null,
};
